/**
 * Agent Hub — Table view (default tab)
 *
 * Shows all agents in a sortable, filterable table.
 * Ownership tabs, status/category filters, search, bulk actions.
 * Always-visible action buttons with tooltips.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Search, Bot, MoreHorizontal, Settings2, Copy, Play, Pause,
  Trash2, Eye, Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AgentStatusBadge, OwnershipBadge, AutonomyBadge, TriggerBadges } from '@/components/agents';
import { AgentIcon } from '@/components/shared/icon-picker';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  AGENT_CATEGORY_LABELS,
  getAgentCategoryStyle,
  type MockAgent,
  type AgentStatus,
  type AgentOwnership,
  type AgentCategory,
} from '@/lib/mock-agents-data';
import { useAgents, useUpdateAgent, useDeleteAgent, useCloneAgent } from '@/lib/use-agents';

export const Route = createFileRoute('/agents/')({
  component: AgentHubPage,
});

type OwnershipFilter = 'all' | AgentOwnership;

function AgentHubPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  // Real-data: custom_agents (49 rows on ai1) adapted to MockAgent VM.
  // Mutations persist to the backend; React Query invalidation
  // (inside each hook's onSuccess) refetches `realAgents` so the table
  // reflects the server — no local throwaway state (#1509).
  const { data: realAgents = [] } = useAgents('all');
  const agents = realAgents;
  const updateAgent = useUpdateAgent();
  const deleteAgent = useDeleteAgent();
  const cloneAgent = useCloneAgent();
  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilter>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | AgentStatus>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | AgentCategory>('all');
  const [deleteTarget, setDeleteTarget] = useState<MockAgent | null>(null);

  // Filter
  const filtered = agents.filter(a => {
    if (ownershipFilter !== 'all' && a.ownership !== ownershipFilter) return false;
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (categoryFilter !== 'all' && a.category !== categoryFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q) || a.tags.some(t => t.includes(q));
  });

  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'name');

  // Actions — all persist to the backend via the shared mutation hooks
  // (same endpoints the agent detail page uses). The list refetches on
  // success, so the table reflects real server state (#1509).
  const handleToggleStatus = (id: string) => {
    const agent = agents.find(a => a.id === id);
    if (!agent || agent.isBuiltin || updateAgent.isPending) return;
    const newStatus: 'active' | 'paused' = agent.status === 'active' ? 'paused' : 'active';
    updateAgent.mutate(
      { id, body: { status: newStatus } },
      {
        onSuccess: () => toast.success(`${agent.name} ${newStatus === 'active' ? 'activated' : 'paused'}`),
        onError: (e) => toast.error(`Couldn't update ${agent.name}`, { description: e.message }),
      },
    );
  };

  const handleClone = (agent: MockAgent) => {
    if (cloneAgent.isPending) return;
    cloneAgent.mutate(
      { id: agent.id },
      {
        onSuccess: (created) =>
          toast.success(`Cloned "${agent.name}"`, {
            description: `Created "${created.name}" as a draft you can edit.`,
          }),
        onError: (e) => toast.error(`Couldn't clone ${agent.name}`, { description: e.message }),
      },
    );
  };

  const handleDelete = () => {
    if (!deleteTarget || deleteAgent.isPending) return;
    const target = deleteTarget;
    deleteAgent.mutate(
      { id: target.id },
      {
        // Keep the dialog open until the server confirms; close + toast
        // only onSuccess so a failed delete leaves the row recoverable.
        onSuccess: () => {
          setDeleteTarget(null);
          toast.success(`${target.name} deleted`);
        },
        onError: (e) => toast.error(`Couldn't delete ${target.name}`, { description: e.message }),
      },
    );
  };

  // Stats
  const activeCount = agents.filter(a => a.status === 'active').length;
  const builtinCount = agents.filter(a => a.isBuiltin).length;
  const customCount = agents.filter(a => !a.isBuiltin).length;

  return (
    <div className="p-6 space-y-6">
      {/* Top bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search agents, tags..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={ownershipFilter} onValueChange={v => setOwnershipFilter(v as OwnershipFilter)}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Owners</SelectItem>
            <SelectItem value="platform">Platform</SelectItem>
            <SelectItem value="company">Company</SelectItem>
            <SelectItem value="team">Team</SelectItem>
            <SelectItem value="personal">Personal</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as 'all' | AgentStatus)}>
          <SelectTrigger className="w-28">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {/* The GET /custom-agents list ALWAYS projects status via
                projectEffectiveStatus, which maps published+active→'active'
                and published+inactive→'paused' — it never returns 'published'.
                A `published` filter option therefore always yields 0 rows
                ("a status the backend can't back up"), so it is omitted; the
                live vocab the list actually contains is draft/active/paused/archived. */}
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={v => setCategoryFilter(v as 'all' | AgentCategory)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(AGENT_CATEGORY_LABELS).map(([key, val]) => (
              <SelectItem key={key} value={key}>{val.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <Badge variant="secondary" className="text-[9px]">{activeCount} active · {builtinCount} builtin · {customCount} custom</Badge>
        </div>
      </div>

      {/* Table */}
      {sorted.length > 0 ? (
        <div className="rounded-lg border overflow-x-auto">
          <div className="min-w-[1000px]">
            <div className="grid grid-cols-[minmax(280px,2fr)_80px_90px_80px_60px_60px_90px_70px_70px] gap-3 p-3 bg-muted/50 text-[10px] uppercase tracking-wider">
              <SortableHeader label="Agent" sortKey="name" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Owner" sortKey="ownership" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Category" sortKey="category" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Tools" sortKey="tools.length" currentSort={sortConfig} onSort={handleSort} className="text-center" />
              <SortableHeader label="Skills" sortKey="skills.length" currentSort={sortConfig} onSort={handleSort} className="text-center" />
              <SortableHeader label="Autonomy" sortKey="autonomyLevel" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Queries" sortKey="metrics.totalQueries" currentSort={sortConfig} onSort={handleSort} className="text-center" />
              <span className="text-right font-semibold text-muted-foreground">Actions</span>
            </div>
            <div className="divide-y">
              {sorted.map(agent => (
                <div
                  key={agent.id}
                  className="grid grid-cols-[minmax(280px,2fr)_80px_90px_80px_60px_60px_90px_70px_70px] gap-3 p-3 items-center hover:bg-muted/30 transition-colors cursor-pointer group"
                  onClick={() => navigate({ to: '/agents/$agentId', params: { agentId: agent.id } })}
                >
                  {/* Agent name + desc */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <AgentIcon iconName={agent.icon} colorName={agent.iconColor} size="sm" />
                      <span className="text-sm font-medium truncate">{agent.name}</span>
                      {agent.isBuiltin && <Lock className="h-2.5 w-2.5 text-muted-foreground shrink-0" />}
                      <Badge variant="secondary" className="text-[9px] shrink-0">v{agent.version}</Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate ml-6">{agent.description}</p>
                  </div>

                  {/* Ownership */}
                  <OwnershipBadge ownership={agent.ownership} />

                  {/* Category */}
                  {/* #1107 — defensive lookup. Server can return category
                       values (e.g. 'general') that are not in the typed
                       union; the pre-fix unguarded index crashed the Hub. */}
                  <Badge variant="outline" className={cn('text-[9px] w-fit', getAgentCategoryStyle(agent.category).color)}>
                    {getAgentCategoryStyle(agent.category).label}
                  </Badge>

                  {/* Status */}
                  <AgentStatusBadge status={agent.status} />

                  {/* Tools */}
                  <p className="text-xs text-center">{agent.tools.length}</p>

                  {/* Skills */}
                  <p className="text-xs text-center">{agent.skills.length}</p>

                  {/* Autonomy */}
                  <AutonomyBadge autonomy={agent.autonomyLevel} />

                  {/* Queries */}
                  <p className="text-xs text-center">{agent.metrics.totalQueries.toLocaleString()}</p>

                  {/* Actions — always visible */}
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit agent"
                      onClick={e => { e.stopPropagation(); navigate({ to: '/agents/$agentId/edit', params: { agentId: agent.id } }); }}>
                      <Settings2 className="h-3.5 w-3.5" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="More actions" onClick={e => e.stopPropagation()}>
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={e => { e.stopPropagation(); navigate({ to: '/agents/$agentId', params: { agentId: agent.id } }); }}>
                          <Eye className="h-3.5 w-3.5 mr-2" /> View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={e => { e.stopPropagation(); handleClone(agent); }}>
                          <Copy className="h-3.5 w-3.5 mr-2" /> Clone
                        </DropdownMenuItem>
                        {!agent.isBuiltin && (
                          <>
                            <DropdownMenuItem onClick={e => { e.stopPropagation(); handleToggleStatus(agent.id); }}>
                              {agent.status === 'active'
                                ? <><Pause className="h-3.5 w-3.5 mr-2" /> Pause</>
                                : <><Play className="h-3.5 w-3.5 mr-2" /> Activate</>}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={e => { e.stopPropagation(); setDeleteTarget(agent); }}>
                              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-center">
          <Bot className="h-8 w-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm font-medium text-muted-foreground">No agents matching your filters</p>
          <p className="text-xs text-muted-foreground/70 mt-1 mb-3">Try adjusting your search or create a new agent</p>
          <Button variant="outline" size="sm" onClick={() => navigate({ to: '/agents/new' })}>
            New Agent
          </Button>
        </div>
      )}

      {/* Delete dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Agent</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
              This will remove the agent and all its execution history. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteAgent.isPending}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteAgent.isPending}>
              {deleteAgent.isPending ? 'Deleting…' : 'Delete Agent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
